import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScanLine, X, ArrowRight } from 'lucide-react';
import { getProductThumb } from '../../data/productImages';
import { Product } from '../../types';
import { productService } from '../../services/productService';
import { useAuth } from '../../context/AuthContext';
import { codiceOemDaMostrare } from '../../utils/oemCodes';

interface SearchCodiciProps {
  variant?: 'compact' | 'hero';
  placeholder?: string;
}

export function SearchCodici({ variant = 'compact', placeholder = 'Ricerca Rapida Codici' }: SearchCodiciProps) {
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Product[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  // Ricerca API con debounce — guardia anti-race (reqIdRef, stesso pattern di
  // AsyncModelSearchSelect.tsx): senza di essa, una risposta più vecchia (es.
  // per "CB435" digitato prima) che arriva DOPO quella più recente (per
  // "CB435A") sovrascriveva i risultati corretti con dati obsoleti/vuoti,
  // causando il flash "appare poi scompare" segnalato dall'utente 26/02/2026.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setOpen(false); return; }

    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await productService.list({ q, limit: 7, publicOnly: true, codeOnly: true, expandOemMatches: true });
        if (myReqId !== reqIdRef.current) return; // risposta obsoleta, ignorata
        setResults(resp.items);
        setOpen(true);
        setSelectedIdx(0);
      } catch {
        if (myReqId === reqIdRef.current) setResults([]);
      } finally {
        if (myReqId === reqIdRef.current) setLoading(false);
      }
    }, 200);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const handleSelect = useCallback((sku: string) => {
    setQuery('');
    setOpen(false);
    // Il click su un risultato porta al catalogo filtrato per quel codice,
    // non più direttamente alla scheda prodotto.
    navigate(`/prodotti/tutti?q=${encodeURIComponent(sku)}`);
  }, [navigate]);

  const handleAdvancedSearch = useCallback(() => {
    setOpen(false);
    navigate(`/prodotti/tutti${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`);
  }, [navigate, query]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      // Invio porta sempre alla lista catalogo con TUTTI i risultati che
      // matchano il testo digitato — non solo il prodotto in evidenza
      // nel dropdown (richiesta utente 10/07/2026).
      handleAdvancedSearch();
    }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  const isHero = variant === 'hero';

  return (
    <div ref={containerRef} className={`relative ${isHero ? 'flex-1' : 'flex-1 min-w-0'}`}>
      <div className={`flex items-center transition-all duration-150 ${
        isHero
          ? 'rounded-lg border border-gray-300 bg-white shadow-sm h-12 px-4 focus-within:border-2 focus-within:border-[hsl(214,58%,30%)] focus-within:ring-2 focus-within:ring-[hsl(214,58%,41%)]/20 hover:border-gray-400'
          : 'rounded-lg border border-gray-400 bg-white shadow-sm h-10 px-3 focus-within:border-2 focus-within:border-[hsl(214,58%,30%)] focus-within:ring-2 focus-within:ring-[hsl(214,58%,41%)]/20 hover:border-[hsl(214,58%,41%)]/50'
        }`}
      >
        <ScanLine strokeWidth={2.4} className={`text-[#006B4F] flex-shrink-0 ${isHero ? 'w-4 h-4' : 'w-[18px] h-[18px]'}`} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); }}
          onFocus={() => { if (query.trim().length >= 2 && results.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`flex-1 h-full bg-transparent outline-none placeholder:text-gray-500 px-2 ${isHero ? 'text-sm' : 'text-sm font-medium'}`}
          data-testid="search-codici-input"
          autoComplete="off"
          aria-label="Cerca codice prodotto"
        />
        {loading && (
          <div className="w-3.5 h-3.5 border border-muted-foreground border-t-transparent rounded-full animate-spin flex-shrink-0 ml-1" />
        )}
        {query && !loading && (
          <button
            onClick={() => { setQuery(''); setOpen(false); inputRef.current?.focus(); }}
            className="text-muted-foreground hover:text-foreground flex-shrink-0 ml-1"
            tabIndex={-1}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div
          className="absolute top-full left-0 min-w-[400px] mt-2 bg-white border rounded-xl shadow-2xl z-[200] overflow-hidden"
          data-testid="search-codici-dropdown"
        >
          <div className="px-4 pt-3 pb-1.5 border-b bg-[hsl(var(--dtk-canvas))]">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              Prodotti trovati ({results.length})
            </span>
          </div>
          {results.map((product, idx) => {
            const isSelected = idx === selectedIdx;
            const oemToShow = codiceOemDaMostrare(product, query);
            return (
              <button
                key={`${product.sku}-${product.matchedOemCode || 'default'}`}
                data-testid="search-codici-result-item"
                onClick={() => handleSelect(product.sku)}
                onMouseEnter={() => setSelectedIdx(idx)}
                className={`w-full flex items-center gap-4 px-4 py-3.5 text-left transition-colors ${
                  isSelected ? 'bg-[hsl(210,40%,93%)]/60' : 'hover:bg-secondary/40'
                }`}
              >
                <img
                  src={getProductThumb(product.sku, product.category)}
                  alt=""
                  className="w-12 h-12 rounded-lg border object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{product.title}</div>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
                    {product.sku}
                    {oemToShow && (
                      <> <span className="mx-1">|</span> OEM: {oemToShow}</>
                    )}
                  </div>
                </div>
                {isAuthenticated && product.listino10 && product.listino10 > 0 && (
                  <div className="text-sm font-bold text-[hsl(214,58%,41%)] flex-shrink-0 tabular-nums">
                    € {product.listino10.toFixed(2).replace('.', ',')}
                  </div>
                )}
              </button>
            );
          })}
          {query.trim() && (
            <div className="px-4 py-2.5 border-t bg-[hsl(var(--dtk-canvas))]">
              <button
                onClick={handleAdvancedSearch}
                className="text-xs text-[hsl(var(--dtk-focus))] font-medium hover:underline flex items-center gap-1"
              >
                Cerca "{query}" nel catalogo avanzato <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {open && query.trim().length >= 2 && results.length === 0 && !loading && (
        <div className="absolute top-full left-0 min-w-[280px] mt-2 bg-white border rounded-xl shadow-2xl z-[200] overflow-hidden">
          <div className="px-4 py-4 text-center">
            <p className="text-sm text-muted-foreground">Nessun prodotto per "{query}"</p>
            <button
              onClick={handleAdvancedSearch}
              className="mt-1.5 text-[11px] text-[hsl(var(--dtk-focus))] font-medium hover:underline flex items-center gap-1 mx-auto"
            >
              Prova la Ricerca Avanzata <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
